import { type, attributes, Model, AttributesMixin, define } from "@type-r/models";
import { getAuthorizationBearer, restfulIO } from "./endpoint";
import { appConfig } from "./appConfig";
import { CommonParticipant } from "./commonParticipant";
import { AssetData, AssetType } from "./assetData";

export const CustomIdentifier = attributes({
    name: String,
    value: String
});

export const Country = attributes({
    id: Number,
    name: String,
    iso2: String,
    iso3: String,
});

export const PolicyType = attributes({
    id: Number,
    name: String,
});

export const CoveredAssetType = attributes({
    id: Number,
    name: String,
});

export const SyndicationRuleType = attributes({
    id: Number,
    name: String,
});

export const PremiumBasisType = attributes({
    id: Number,
    name: String,
});

export const IndemnityType = attributes({
    id: Number,
    name: String,
});

export const ClaimFilingPeriodPerilCoverPeriodType = attributes({
    id: Number,
    name: String,
});

export const WaitPeriodPerilCoverPeriodType = attributes({
    id: Number,
    name: String,
});

export const LiabilityLimitType = attributes({
    id: Number,
    name: String,
});

export const PolicyPremiumType = attributes({
    id: Number,
    name: String,
});

export const MaxPaymentTermsType = attributes({
    id: Number,
    name: String,
});

export const ScopeOfCoverageType = attributes({
    id: Number,
    name: String,
});

export const PolicyAttachmentType = attributes({
    id: Number,
    name: String,
});

export const PerilCoverageType = attributes({
    id: Number,
    name: String,
});

export const PolicyDeductibleType = attributes({
    id: Number,
    name: String,
});

export const MaxExtensionPeriodType = attributes({
    id: Number,
    name: String,
});

export const MinBuyerValueOptType = attributes({
    id: Number,
    name: String,
});

export const DepositBuyerValueOptType = attributes({
    id: Number,
    name: String,
});

export const ReportPeriodType = attributes({
    id: Number,
    name: String,
});

export const CancellableLimitType = attributes({
    id: Number,
    name: String,
});

export type PolicyDocumentType = InstanceType<typeof PolicyDocumentType>;
export const PolicyDocumentType = attributes({
    id: Number,
    name: String,
});

export const PolicyInsuranceCarrier = attributes({
    ...CommonParticipant.attributes,
    serialId: Number
});

export const Obligor = attributes({
    ...CommonParticipant.attributes,
    customIdentifiers: type([ CustomIdentifier ]).value([ {} ]),
});

export const PerilCoverageClaimFilingPeriod = attributes({
    serialId: Number,
    perilCoverageType: type(PerilCoverageType).value({}),
    claimFilingPeriod: Number
});

export const PerilCoverageWaitingPeriod = attributes({
    serialId: Number,
    perilCoverageType: type(PerilCoverageType).value({}),
    waitingPeriod: Number
});

export const PremiumRptPeriod = attributes({
    serialId: Number,
    dateStart: Date,
    dateEnd: Date,
    utilization: Number,
    premiumAccrued: Number,
    premiumDue: Number,
});

export const PolicyPremiumSchedule = attributes({
    serialId: Number,
    minBuyerValueOptType: type(MinBuyerValueOptType).value({}),
    minimumPremium: Number,
    depositBuyerValueOptType: type(DepositBuyerValueOptType).value({}),
    depositPremium: Number,
    premiumPaymentTerms: Number,
    reportingDeadlineInDays: Number,
    amountPastDueRptThreshold: Number,
    daysPastDueRptThreshold: Number,
    reportPeriodType: type(ReportPeriodType).value({}),
    numberOfPeriods: Number,
    premiumRptPeriodDetails: type([ PremiumRptPeriod ]).value([ {} ])
});

export const PolicyCountrySchedule = attributes({
    serialId: Number,
    country: type(Country).value({}),
    countryGroup: String,
    limit: Number,
    indemnity: type(Number).value(0),
    premiumRate: type(Number).value(0),
    estLongestPaymentTerms: Number,
    maxExstensionPeriod: Number,
    claimFilingPeriodAllRisk: Number,
    claimFilingPeriodInsolvency: Number,
    claimFilingPeriodProtractedDefault: Number,
    claimFilingPoliticalRisk: Number,
    waitingPeriodAllRisk: Number,
    waitingPeriodInsolvency: Number,
    waitingPeriodProtractedDefault: Number,
    waitingPeriodPoliticalRisk: Number,
    discretionaryCreditLimit: Number,
    deductibleAmount: Number,
    specialCondition: String,
    nonQualifyingLoss: Number,
    maxDiscretionCreditLimit: Number

});

export const PolicyGoverningLaw = attributes({
    serialId: Number,
    governingLawId: Number,
    name: String,
});

export const PolicyTransactionCurrencyCode = attributes({
    code: String,
});

export const DealTeamMember = attributes({
    userId: Number,
    userName: String,
});

export const CollateralAgent = attributes({
    firstName: String,
    lastName: String,
    email: String,
    phone: String,
    company: String,
});

export interface PolicyDocument extends AttributesMixin<typeof PolicyDocument> { }
@define
export class PolicyDocument extends Model {
    static attributes = {
        serialId: Number,
        policyDocumentType: type(PolicyDocumentType).value({}),
        fileName: String,
        fileGuid: String,
        documentPath: String,
        description: String,
        fileHash: String,
    };

    async save(options: { params: { file } }) {
        const { file } = options.params;

        let path: string = `api/Upload?productCode=INS&assetType=newpolicy&description=&assetId=0&stateId=1&state=Draft&policydocumenttypeid=2`;
        const url = appConfig.fileStorageApiUrl + path;
        const formData = new FormData();
        formData.append('', file, file.name);
        const response = await fetch(url, {
            method: 'POST',
            headers: new Headers({
                "Authorization": getAuthorizationBearer()
            }),
            body: formData
        });

        if (response.status === 413) {
            throw new Error('File size must be less than 2MB');
        }

        if (response.status < 200 || response.status >= 300) {
            throw new Error(response.statusText || `HTTP Error ${response.status}`);
        }

        const json = await response.json();

        this.set({
            fileGuid: json.uploadedFiles[ 0 ].rowKey
        });
        return this;
    }

    async download() {
        let path: string = `api/Download?productCode=INS&assetType=newpolicy&assetId=0&rowKey=${encodeURIComponent(this.fileGuid)}`;
        const url = appConfig.fileStorageApiUrl + path;

        const response = await fetch(url, {
            method: 'GET',
            headers: new Headers({
                "Authorization": getAuthorizationBearer()
            })
        });

        const blob = await response.blob();

        const dataUrl = window.URL.createObjectURL(blob),
            a = document.createElement('a');

        a.href = dataUrl;
        a.download = this.fileName;
        document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
        a.click();
        a.remove();  //afterwards we remove the element again
    }
}

export interface PolicyBuyerSchedule extends AttributesMixin<typeof PolicyBuyerSchedule> {
}
@define
export class PolicyBuyerSchedule extends Model {
    hasErrors = false;

    static attributes = {
        obligor: type(Obligor).value({}),
        maxCreditLimit: Number,
        state: String,
        maxPaymentTerms: Number,
        indemnity: Number,
        premiumRate: Number,
        estUtilization: Number,
        attachmentPeriodStartDate: Date,
        attachmentPeriod: Number,
        seller: String,
        descriptionOfGoods: String,
        minimumPremium: Number,
        depositPremium: Number,
        pastDueDescription: String,
        limitCurrency: String,
        specialConditionDescription: String,
        isCancellable: Boolean,
        hasPastDueHistory: Boolean,

        //not on the model
        buyerName: String,
        buyerDUNS: String,
        buyerSpecialConditions: Boolean,
        errors: type(Object).dontSave
    }

    required(propertyName) {
        if (this[ propertyName ] == null || this[ propertyName ] == undefined || (typeof this[ propertyName ] == 'string' && this[ propertyName ] == '')) {
            this.errors[ propertyName ] = "Required"
            this.hasErrors = true;
        } else {
            this.errors[ propertyName ] = null;
        }
    }

    isValidBuyer() {
        return !this.hasErrors;
    }

    validateBuyer() {
        this.hasErrors = false;
        this.required('maxCreditLimit');
        this.required('state');
        this.required('maxPaymentTerms');
        this.required('indemnity');
        this.required('premiumRate');
        this.required('estUtilization');
        this.required('attachmentPeriod');
        this.required('attachmentPeriodStartDate');
        this.required('limitCurrency');
        this.required('isCancellable');
        return !this.hasErrors;
    }
}

export interface Policy extends AttributesMixin<typeof Policy> { }
@define
export class Policy extends AssetData {
    static endpoint = restfulIO('api/v1/ingest/policy');

    static attributes = {
        ...AssetData.attributes,
        assetType: type(AssetType).value({ id: 3, name: 'InsurancePolicy' }),
        lqxPolicyNumber: String,
        name: String,
        carrierPolicyNumber: String,
        inceptionDate: Date,
        expiryDate: Date,
        broker: type(CommonParticipant).value({}),
        platformBroker: type(CommonParticipant).value({}),
        policyInsuranceCarriers: type([ PolicyInsuranceCarrier ]).value([ {} ]),
        insuredParty: type(CommonParticipant).value({}),
        lossPayee: type(CommonParticipant).value({}),
        additionalInsuredName: type(CommonParticipant).value({}),
        policyType: type(PolicyType).value({}),
        coveredAssetType: type(CoveredAssetType).value({}),
        policyCommissionRate: Number,
        isSyndicated: Boolean,
        syndicationRuleType: type(SyndicationRuleType).value({}),
        premiumBasisType: type(PremiumBasisType).value({}),
        indemnityType: type(IndemnityType).value({}),
        claimFilingPeriodPerilCoverPeriodType: type(ClaimFilingPeriodPerilCoverPeriodType).value({}),
        waitPeriodPerilCoverPeriodType: type(WaitPeriodPerilCoverPeriodType).value({}),
        autoExtension: Number,
        liabilityLimitType: type(LiabilityLimitType).value({}),
        liabilityLimit: Number,
        indemnity: type(Number).value(0),
        premiumRate: Number,
        policyPremiumType: type(PolicyPremiumType).value({}),
        currencyCode: String,
        maxPaymentTermsType: type(MaxPaymentTermsType).value({}),
        maxPaymentTerms: Number,
        scopeOfCoverageType: type(ScopeOfCoverageType).value({}),
        policyAttachmentType: type(PolicyAttachmentType).value({}),
        perilCoverageClaimFilingPeriods: type([ PerilCoverageClaimFilingPeriod ]).value([ {} ]),
        perilCoverageWaitingPeriods: type([ PerilCoverageWaitingPeriod ]).value([ {} ]),
        invoiceTerms: Number,
        nonQualifyingLoss: Number,
        policyDeductibleType: type(PolicyDeductibleType).value({}),
        deductibleAmount: Number,
        discretionaryCreditLimit: Number,
        cancellableLimitType: type(CancellableLimitType).value({}),
        maxExtensionPeriod: Number,
        policyGoverningLaws: type([ PolicyGoverningLaw ]).value([ {} ]),
        policyTransactionCurrencyCodes: type([ PolicyTransactionCurrencyCode ]).value([ '' ]),
        policyPremiumSchedule: type(PolicyPremiumSchedule).value({}),
        policyBuyerSchedules: type([ PolicyBuyerSchedule ]).value([]),
        policyCountrySchedules: type([ PolicyCountrySchedule ]).value([ {} ]),
        policyDocuments: type([ PolicyDocument ]).value([]),
        dealTeam: type([ DealTeamMember ]).value([ {} ]),
        collateralAgent: type(CollateralAgent).value({}),

        //TODO: not on model
        // utilization: Number,
        havingDiscretionCreditLimit: Boolean,
        // estMaxPayTermsLiabLimitType: type(EntityType).value({}),
        maxExtensionPeriodType: type(MaxExtensionPeriodType).value({}),
        // perilCoverageWaitingPeriod: type([ PerilCoverageClaimFilingPeriod ]).value([ {} ]),
    }
    isLoaded: boolean;


    onLoaded() {
        const defaultValues = new Policy();
        this.set({ assetType: defaultValues.assetType });
        if (!this.uid) this.set({ uid: defaultValues.uid });
        if (!this.sourceAsset) this.set({ sourceAsset: defaultValues.sourceAsset.clone() });
        if (!this.state) this.set({ state: defaultValues.state.clone() });
        if (!this.broker) this.set({ broker: defaultValues.broker.clone() });
        if (!this.platformBroker) this.set({ platformBroker: defaultValues.platformBroker.clone() });
        if (!this.policyInsuranceCarriers || this.policyInsuranceCarriers.length <= 0) this.set({ policyInsuranceCarriers: defaultValues.policyInsuranceCarriers.clone() });
        if (!this.insuredParty) this.set({ insuredParty: defaultValues.insuredParty.clone() });
        if (!this.lossPayee) this.set({ lossPayee: defaultValues.lossPayee.clone() });
        if (!this.additionalInsuredName) this.set({ additionalInsuredName: defaultValues.additionalInsuredName.clone() });
        if (!this.policyType) this.set({ policyType: defaultValues.policyType.clone() });
        if (!this.coveredAssetType) this.set({ coveredAssetType: defaultValues.coveredAssetType.clone() });
        if (!this.syndicationRuleType) this.set({ syndicationRuleType: defaultValues.syndicationRuleType.clone() });
        if (!this.premiumBasisType) this.set({ premiumBasisType: defaultValues.premiumBasisType.clone() });
        if (!this.indemnityType) this.set({ indemnityType: defaultValues.indemnityType.clone() });
        if (!this.claimFilingPeriodPerilCoverPeriodType) this.set({ claimFilingPeriodPerilCoverPeriodType: defaultValues.claimFilingPeriodPerilCoverPeriodType.clone() });
        if (!this.waitPeriodPerilCoverPeriodType) this.set({ waitPeriodPerilCoverPeriodType: defaultValues.waitPeriodPerilCoverPeriodType.clone() });
        if (!this.liabilityLimitType) this.set({ liabilityLimitType: defaultValues.liabilityLimitType.clone() });
        if (!this.policyPremiumType) this.set({ policyPremiumType: defaultValues.policyPremiumType.clone() });
        if (!this.maxPaymentTermsType) this.set({ maxPaymentTermsType: defaultValues.maxPaymentTermsType.clone() });
        if (!this.scopeOfCoverageType) this.set({ scopeOfCoverageType: defaultValues.scopeOfCoverageType.clone() });
        if (!this.policyAttachmentType) this.set({ policyAttachmentType: defaultValues.policyAttachmentType.clone() });
        if (!this.perilCoverageClaimFilingPeriods) this.set({ perilCoverageClaimFilingPeriods: defaultValues.perilCoverageClaimFilingPeriods.clone() });
        if (!this.perilCoverageWaitingPeriods) this.set({ perilCoverageWaitingPeriods: defaultValues.perilCoverageWaitingPeriods.clone() });
        if (!this.policyDeductibleType) this.set({ policyDeductibleType: defaultValues.policyDeductibleType.clone() });
        if (!this.cancellableLimitType) this.set({ cancellableLimitType: defaultValues.cancellableLimitType.clone() });
        if (!this.policyGoverningLaws || this.policyGoverningLaws.length <= 0) this.set({ policyGoverningLaws: defaultValues.policyGoverningLaws.clone() });
        if (!this.policyTransactionCurrencyCodes) this.set({ policyTransactionCurrencyCodes: defaultValues.policyTransactionCurrencyCodes.clone() });
        if (!this.policyPremiumSchedule) this.set({ policyPremiumSchedule: defaultValues.policyPremiumSchedule.clone() });
        if (!this.policyBuyerSchedules || this.policyBuyerSchedules.length <= 0) this.set({ policyBuyerSchedules: defaultValues.policyBuyerSchedules.clone() });
        if (!this.policyCountrySchedules || this.policyCountrySchedules.length <= 0) this.set({ policyCountrySchedules: defaultValues.policyCountrySchedules.clone() });
        if (!this.policyDocuments) this.set({ policyDocuments: defaultValues.policyDocuments.clone() });
        if (!this.dealTeam) this.set({ dealTeam: defaultValues.dealTeam.clone() });
        if (!this.collateralAgent) this.set({ collateralAgent: defaultValues.collateralAgent.clone() });
        if (!this.maxExtensionPeriodType) this.set({ maxExtensionPeriodType: defaultValues.maxExtensionPeriodType.clone() });

        if (!this.indemnity) this.set({ indemnity: defaultValues.indemnity });
        if (!this.premiumRate) this.set({ premiumRate: defaultValues.premiumRate });

        this.set({ state: { id: 1, name: 'Draft' } });

        this.isLoaded = true;
    }

    async uploadDocument(file, documentType) {
        let document: PolicyDocument = null;
        try {
            [ document ] = this.policyDocuments.add({
                fileName: file.name,
                policyDocumentType: documentType
            });

            await document.save({
                params: { file }
            });
        } catch {
            this.policyDocuments.remove(document);
        }
    }
}
